import {
  AuthMethodEnum,
  CreateSessionRequestEnum,
  CreateSessionResponseEnum,
  GetSessionInfoResponseEnum,
  SessionInfo,
} from "@b2bportal/auth-api";
import { trackEvent } from "@hopper-b2b/api";
import { createSession, fetchSessionInfo } from "../api";
import { getNativeUserIdFromCookies } from "../api/webView";
import { AuthTrackingEvents } from "../types/tracking";
import { updateRefreshToken } from "./useRefreshToken";

export const createAnonymousSession = async (
  locale: string,
  nativeUserId?: string
) => {
  return createSession({
    authMethod: nativeUserId
      ? {
          AuthMethod: AuthMethodEnum.AnonymousHopperApp,
          locale: locale,
          userId: nativeUserId,
        }
      : {
          AuthMethod: AuthMethodEnum.Anonymous,
          locale: locale,
        },
    CreateSessionRequest: CreateSessionRequestEnum.SignIn,
  });
};
export type LoginCallback = (sessionInfo: SessionInfo) => void;
export type ErrorCallback = () => void;

export const fetchSessionAndSetSession = async (
  callback: LoginCallback,
  errorCallback?: ErrorCallback
) => {
  const res = await fetchSessionInfo();
  if (res.GetSessionInfoResponse === GetSessionInfoResponseEnum.Session) {
    callback(res.sessionInfo);
  } else {
    errorCallback?.();
  }
};

const SESSION_INFO_MAX_RETRIES = 2;
const SESSION_INFO_RETRY_DELAY_MS = 1000;

const fetchSessionInfoWithRetry = async (
  retries = SESSION_INFO_MAX_RETRIES
) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetchSessionInfo();
    } catch (error) {
      if (attempt < retries) {
        console.warn(
          `Fetch session info failed (attempt ${attempt + 1}/${retries + 1}), retrying...`
        );
        await new Promise((resolve) =>
          setTimeout(resolve, SESSION_INFO_RETRY_DELAY_MS)
        );
      } else {
        throw error;
      }
    }
  }
  // This should never be reached, but satisfies TypeScript
  throw new Error("Unexpected: exhausted retries without result");
};

export const fetchOrCreateSession = async (
  onLogin: LoginCallback,
  locale: string
) => {
  const createAndFetchSession = async () => {
    const nativeUserId = getNativeUserIdFromCookies();
    const createSessionRes = await createAnonymousSession(locale, nativeUserId);
    if (
      createSessionRes.CreateSessionResponse ===
      CreateSessionResponseEnum.CreateSessionSuccess
    ) {
      return fetchSessionAndSetSession(onLogin);
    } else {
      console.error("Create Session failed");
    }
  };

  try {
    const sessionInfoRes = await fetchSessionInfoWithRetry();
    if (
      sessionInfoRes.GetSessionInfoResponse ===
        GetSessionInfoResponseEnum.NoSession ||
      sessionInfoRes.GetSessionInfoResponse ===
        GetSessionInfoResponseEnum.SessionRejected
    ) {
      // In typical cases, token expiration will result in a NoSession. However,
      // when there's a transient issue with BiFrost, a SessionRejected error will also
      // occur, so we treat these cases the same.
      const result = await updateRefreshToken();
      if (result === "INVALID") {
        // If we're unable to refresh the token create a new anonymous session.
        // This should only occur if the refresh token is invalid.
        await createAndFetchSession();
      } else {
        await fetchSessionAndSetSession(onLogin);
      }
    } else if (
      sessionInfoRes.GetSessionInfoResponse ===
      GetSessionInfoResponseEnum.Session
    ) {
      onLogin(sessionInfoRes.sessionInfo);
    }
  } catch (error) {
    console.error(error);
    trackEvent({
      eventName: AuthTrackingEvents.Failure,
      properties: {
        message: `session_info failed after ${SESSION_INFO_MAX_RETRIES + 1} attempts: ${error instanceof Error ? error.message : String(error)}`,
      },
    });
    // If fetching session info fails after retries (e.g., persistent 500),
    // fall back to creating a new anonymous session so the app doesn't get
    // stuck in a permanent loading state.
    await createAndFetchSession();
  }
};
